/**
 * The in-page dialog for a rule review decision (DQ rule governance): an approval (optional note), a rejection (a
 * written reason is REQUIRED, decision 5) or a platform admin's override (a justification of at least 30 characters
 * and an optional ticket reference, decision 4). The server re-checks every rule; this only collects the text and
 * says what is missing before it is sent.
 */
import { useEffect, useRef, useState } from "react";
import { ShieldAlert, ThumbsDown, ThumbsUp, X } from "lucide-react";

import { OVERRIDE_MIN_JUSTIFICATION } from "../../lib/ruleGovernance";
import { useFocusTrap } from "../../shared/hooks/use-focus-trap";

export type ReviewKind =
  | "submit"
  | "approve"
  | "confirm"
  | "reject"
  | "override";

const TITLES: Record<ReviewKind, string> = {
  submit: "Submit for approval",
  approve: "Approve rule",
  confirm: "Confirm approval",
  reject: "Reject rule",
  override: "Approve by override",
};

export function ReviewDialog({
  kind,
  ruleLabel,
  busy = false,
  error = null,
  onCancel,
  onSubmit,
}: {
  kind: ReviewKind;
  ruleLabel: string;
  busy?: boolean;
  /** The server's answer to a failed attempt; the dialog stays open with the text kept. */
  error?: string | null;
  /** The caller ignores it while `busy` (never close mid-request). */
  onCancel: () => void;
  onSubmit: (text: string, ticketRef: string) => void;
}) {
  const [text, setText] = useState("");
  const [ticket, setTicket] = useState("");
  // Same conventions as RuleCodePanel: focus trapped in the dialog, Escape closes it.
  const containerRef = useFocusTrap(true);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const trimmed = text.trim();
  const problem =
    kind === "reject" && !trimmed
      ? "A written reason is required."
      : kind === "override" && trimmed.length < OVERRIDE_MIN_JUSTIFICATION
        ? `A justification of at least ${OVERRIDE_MIN_JUSTIFICATION} characters is required (${trimmed.length} so far).`
        : null;
  const label =
    kind === "reject"
      ? "Reason for the author"
      : kind === "override"
        ? "Justification"
        : kind === "submit"
          ? "Note for the reviewer (optional)"
          : "Note (optional)";
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={TITLES[kind]}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div
        ref={containerRef}
        className="glass-surface w-full max-w-lg rounded-xl p-5 space-y-3"
      >
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-sm font-semibold text-white">
            {TITLES[kind]}: {ruleLabel}
          </h3>
          <button
            aria-label="Close"
            onClick={onCancel}
            disabled={busy}
            className="text-white/50 hover:text-white/80 disabled:opacity-40"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {kind === "override" && (
          <p className="text-xs text-amber-200/90 flex gap-2">
            <ShieldAlert className="w-4 h-4 shrink-0" aria-hidden />
            Emergency approval without a second reviewer. It is recorded as an
            override in the audit trail, the project&apos;s Data Stewards are
            notified, and the rule is marked &quot;Approved by override&quot;
            until a steward confirms it.
          </p>
        )}
        {kind === "submit" && (
          <p className="text-xs text-white/60">
            A Data Steward other than you reviews it. Until it is approved,
            profiling runs keep using the version in force (the pack original,
            or this rule&apos;s previously approved version).
          </p>
        )}
        {(kind === "approve" || kind === "confirm") && (
          <p className="text-xs text-white/60">
            {kind === "confirm"
              ? "Confirming makes this a normal four-eyes approval and removes its marker."
              : "Approved rules count in the project's next profiling run."}
          </p>
        )}
        <label className="block text-xs text-white/60">
          {label}
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            className="mt-1 w-full rounded-lg bg-surface-300/50 border border-white/10 p-2 text-sm text-white focus:outline-none focus:border-[var(--primary)]"
          />
        </label>
        {kind === "override" && (
          <label className="block text-xs text-white/60">
            Incident / ticket reference (optional)
            <input
              value={ticket}
              onChange={(e) => setTicket(e.target.value)}
              maxLength={100}
              className="mt-1 w-full rounded-lg bg-surface-300/50 border border-white/10 p-2 text-sm text-white focus:outline-none focus:border-[var(--primary)]"
            />
          </label>
        )}
        {problem && <p className="text-xs text-amber-300">{problem}</p>}
        {error && (
          <p role="alert" className="text-xs text-red-300">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded-md text-white/70 hover:bg-white/5 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            onClick={() => onSubmit(trimmed, ticket.trim())}
            disabled={problem !== null || busy}
            className={`text-xs px-3 py-1.5 rounded-md font-medium inline-flex items-center gap-1 disabled:opacity-40 ${
              kind === "reject"
                ? "bg-red-500/20 text-red-200 hover:bg-red-500/30"
                : kind === "override"
                  ? "bg-amber-500/20 text-amber-100 hover:bg-amber-500/30"
                  : "bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/30"
            }`}
          >
            {kind === "reject" ? (
              <ThumbsDown className="w-3.5 h-3.5" />
            ) : (
              <ThumbsUp className="w-3.5 h-3.5" />
            )}
            {busy ? "Saving…" : TITLES[kind]}
          </button>
        </div>
      </div>
    </div>
  );
}
